"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { GripVertical } from "lucide-react";
import { useResizableSidePanel } from "@/hooks/useResizableSidePanel";
import { SourceDocumentPanelBody } from "./SourceDocumentPanelBody";

const PANEL_MIN_PX = 320;
const PANEL_DEFAULT_PX = 640;
const PANEL_WIDTH_STORAGE_KEY = "stockline:ai-source-panel-width";

interface SourceDocumentPanelContextValue {
  /** Open the side panel for a proposal batch's source document. */
  openSource: (batchId: string) => void;
}

const SourceDocumentPanelContext =
  createContext<SourceDocumentPanelContextValue | null>(null);

/**
 * Optional accessor — returns null when no provider is mounted, letting
 * callers fall back to the legacy fixed overlay so hosts that don't opt into
 * the push layout keep working unchanged.
 */
export function useSourceDocumentPanel(): SourceDocumentPanelContextValue | null {
  return useContext(SourceDocumentPanelContext);
}

/**
 * Hosts a resizable, page-pushing source-document side panel — the same model
 * as the recap workspace ChatPanel (`RecapWorkspaceLayout`): the content is
 * reflowed via `margin-right` (not overlaid), the panel is `fixed` to the
 * viewport's right edge, and its width is drag-resizable + persisted.
 *
 * `contentClassName` is applied to the wrapper around `children` so callers can
 * preserve their height model (e.g. `"h-full"` for the full-height chat tab,
 * nothing for the document-flow proposals page).
 */
export function SourceDocumentPanelProvider({
  children,
  contentClassName = "",
}: {
  readonly children: ReactNode;
  readonly contentClassName?: string;
}) {
  // `activeBatchId` drives open/close; `renderedBatchId` is sticky so the body
  // keeps showing its content through the slide-out animation after close.
  const [activeBatchId, setActiveBatchId] = useState<string | null>(null);
  const [renderedBatchId, setRenderedBatchId] = useState<string | null>(null);

  const openSource = useCallback((batchId: string) => {
    setActiveBatchId(batchId);
    setRenderedBatchId(batchId);
  }, []);
  const close = useCallback(() => setActiveBatchId(null), []);

  // Drag-resize + persisted width + slide-in animation are shared with the
  // recap workspace ChatPanel via useResizableSidePanel.
  const {
    currentWidth,
    isDragging,
    hasBeenOpened,
    panelVisible,
    handleMouseDown,
    handleTouchStart,
    handleKeyDown,
  } = useResizableSidePanel({
    minPx: PANEL_MIN_PX,
    defaultPx: PANEL_DEFAULT_PX,
    storageKey: PANEL_WIDTH_STORAGE_KEY,
    isOpen: activeBatchId !== null,
  });

  // Stable context value — openSource is itself stable (useCallback), so this
  // only re-creates if that identity ever changes.
  const contextValue = useMemo(() => ({ openSource }), [openSource]);

  return (
    <SourceDocumentPanelContext.Provider value={contextValue}>
      <div
        className={contentClassName}
        style={{
          marginRight: hasBeenOpened && panelVisible ? currentWidth : 0,
          transition: isDragging ? "none" : "margin-right 300ms ease-out",
        }}
      >
        {children}
      </div>
      {hasBeenOpened && (
        <div
          className="fixed bottom-0 right-0 top-[--impersonation-banner-height] border-l border-gray-200 bg-white z-40 flex shadow-[-8px_0_24px_-8px_rgba(0,0,0,0.12)]"
          style={{
            width: currentWidth,
            transform: panelVisible ? "translateX(0)" : "translateX(100%)",
            transition: isDragging ? "none" : "transform 300ms ease-out",
          }}
        >
          <button
            type="button"
            data-testid="source-panel-drag-handle"
            aria-label="Resize source document panel"
            className="w-3 flex-shrink-0 cursor-col-resize hover:bg-gray-100 active:bg-gray-200 transition-colors flex items-center justify-center group touch-manipulation appearance-none border-none bg-transparent p-0"
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            onKeyDown={handleKeyDown}
          >
            <GripVertical className="h-4 w-4 text-gray-300 group-hover:text-gray-400" />
          </button>
          {/* flex-col + min-h-0 so the body (AttachmentPreviewPanel → ExcelViewer)
              gets a bounded height and its inner overflow-auto can actually
              scroll, instead of growing to content height and being clipped. */}
          <div className="flex flex-1 flex-col overflow-hidden min-h-0">
            {renderedBatchId && (
              <SourceDocumentPanelBody
                key={renderedBatchId}
                batchId={renderedBatchId}
                open={panelVisible}
                onClose={close}
              />
            )}
          </div>
        </div>
      )}
    </SourceDocumentPanelContext.Provider>
  );
}
